import type { ClassifyResult, LabelingSession, LabelingSessionImage, ImageLabel, LabelingExperiment, LabelingExperimentLabels } from '../types/LabelingTypes';
import type { HttpClient } from '@/lib/http/HttpClient';

export async function labelizeListExperiments(
  http: HttpClient,
  signal?: AbortSignal
): Promise<LabelingExperiment[]> {
  const result = await http.get<{ timestamp: string; experiments: LabelingExperiment[] }>(
    '/api/labelisation/experiments',
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to list experiments');
  return result.data.experiments;
}

type WirePrediction = { imageKey: string; predictedLabel: string; confidence: number };

// Worker labels are 'ok'/'worn'; the labeling UI's CnnLabel is 'good'/'worn'.
function toClassifyResult(prediction: WirePrediction): ClassifyResult {
  return {
    key: prediction.imageKey,
    label: prediction.predictedLabel === 'worn' ? 'worn' : 'good',
    confidence: prediction.confidence,
  };
}

export async function labelizeClassify(http: HttpClient, key: string): Promise<ClassifyResult> {
  const result = await http.post<{ timestamp: string; prediction: WirePrediction }>('/api/label-prediction/predict', {
    imageKey: key,
  });
  if (!result.ok) throw new Error(result.message ?? 'Failed to classify image');
  return toClassifyResult(result.data.prediction);
}

export async function labelizeClassifyBatch(
  http: HttpClient,
  keys: string[]
): Promise<ClassifyResult[]> {
  const result = await http.post<{ timestamp: string; predictions: WirePrediction[] }>(
    '/api/label-prediction/predict/batch',
    { images: keys.map((imageKey) => ({ imageKey })) }
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to classify images');
  return result.data.predictions.map(toClassifyResult);
}

export async function labelizeListSessions(
  http: HttpClient,
  toolId: string,
  signal?: AbortSignal
): Promise<LabelingSession[]> {
  const result = await http.get<{ timestamp: string; sessions: LabelingSession[] }>(
    `/api/labelisation/sessions?experimentId=${encodeURIComponent(toolId)}`,
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to list sessions');
  return result.data.sessions;
}

export async function labelizeCreateSession(
  http: HttpClient,
  toolId: string,
  signal?: AbortSignal
): Promise<LabelingSession> {
  const result = await http.post<{ timestamp: string; session: LabelingSession }>(
    '/api/labelisation/sessions',
    { experimentId: toolId, name: 'default', description: null },
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to create session');
  return result.data.session;
}

export async function labelizeListSessionImages(
  http: HttpClient,
  sessionId: string,
  signal?: AbortSignal
): Promise<LabelingSessionImage[]> {
  const result = await http.get<{ timestamp: string; sessionId: string; images: LabelingSessionImage[] }>(
    `/api/labelisation/sessions/${encodeURIComponent(sessionId)}/images`,
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to list session images');
  return result.data.images;
}

/**
 * Every annotator's labels for one experiment, within the caller's company. Read-only: unlike the
 * session routes, which only ever see the caller's own sessions, this one exists for the export.
 */
export async function labelizeListExperimentLabels(
  http: HttpClient,
  experimentId: string,
  signal?: AbortSignal
): Promise<LabelingExperimentLabels> {
  const result = await http.get<{ timestamp: string } & LabelingExperimentLabels>(
    `/api/labelisation/experiments/${encodeURIComponent(experimentId)}/labels`,
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to list experiment labels');
  const { sessions, images } = result.data;
  return { experimentId, sessions, images };
}

/**
 * Re-signs a single image URL. Presigned RustFS URLs expire (~15 min), so an
 * `<img>` can start failing mid-session — the UI calls this on load failure.
 */
export async function labelizeGetSignedImageUrl(
  http: HttpClient,
  experimentId: string,
  partId: string,
  operationId: string,
  imageKey: string,
  signal?: AbortSignal
): Promise<string> {
  // imageKey disambiguates the few (experiment, part, operation) triples that hold
  // several stacks; as a query param because S3 keys contain slashes (LAB-5).
  const result = await http.get<{ timestamp: string; url: string; expiresAt: string }>(
    `/api/labelisation/experiments/${encodeURIComponent(experimentId)}/images/${encodeURIComponent(partId)}/${encodeURIComponent(operationId)}/url?imageKey=${encodeURIComponent(imageKey)}`,
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to refresh image URL');
  return result.data.url;
}

export async function labelizeSaveLabels(
  http: HttpClient,
  sessionId: string,
  labels: Record<string, ImageLabel | null>,
  signal?: AbortSignal
): Promise<{ sessionId: string; updatedCount: number }> {
  const payload = Object.entries(labels)
    .filter(([, label]) => label !== null)
    .map(([imageKey, label]) => ({ imageKey, label: label as ImageLabel }));

  const result = await http.put<{ timestamp: string; sessionId: string; updatedCount: number }>(
    `/api/labelisation/sessions/${encodeURIComponent(sessionId)}/labels`,
    { labels: payload },
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to save labels');
  return { sessionId: result.data.sessionId, updatedCount: result.data.updatedCount };
}

/**
 * Deletes the label of a single image. The image key travels as a path parameter — the
 * shared `HttpClient.delete()` cannot carry a request body, so a body-based shape would be
 * unreachable from the frontend. Fails (404) when the image carries no label.
 */
export async function labelizeDeleteLabel(
  http: HttpClient,
  sessionId: string,
  imageKey: string,
  signal?: AbortSignal
): Promise<{ success: boolean }> {
  // A query param, not a path segment: RustFS image keys contain slashes, and nginx's
  // proxy_pass normalizes %2F-encoded slashes in path segments before forwarding — a query
  // string passes through untouched.
  const result = await http.delete<{ timestamp: string; success: boolean }>(
    `/api/labelisation/sessions/${encodeURIComponent(sessionId)}/labels?imageKey=${encodeURIComponent(imageKey)}`,
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to delete label');
  return { success: result.data.success };
}

export type StoredPrediction = {
  imageKey: string;
  predictedLabel: string;
  confidence: number;
  modelName: string;
  modelVersion: string;
  updatedAt: string;
};

export async function labelizeListPredictions(
  http: HttpClient,
  experimentId: string,
  signal?: AbortSignal
): Promise<StoredPrediction[]> {
  const result = await http.get<{ timestamp: string; experimentId: string; predictions: StoredPrediction[] }>(
    `/api/labelisation/experiments/${encodeURIComponent(experimentId)}/predictions`,
    signal ? { signal } : undefined
  );
  if (!result.ok) throw new Error(result.message ?? 'Failed to list stored predictions');
  return result.data.predictions;
}
